import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Navbar } from '../components/Navbar'
import { SiteFooter } from '../sections/SiteFooter'
import { FadeIn } from '../components/ui/FadeIn'
import { GlassCard } from '../components/ui/GlassCard'
import { Mail, Send, CheckCircle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

export function ContactPage() {
  const { t } = useLanguage()
  
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simular envío del formulario
    await new Promise((resolve) => setTimeout(resolve, 1000))
    
    setIsSubmitting(false)
    setIsSubmitted(true)
    
    // Resetear después de 3 segundos
    setTimeout(() => {
      setIsSubmitted(false)
      setFormData({ name: '', email: '', company: '', message: '' })
    }, 3000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className="min-h-screen bg-nexus-black text-white">
      <Navbar />
      <main className="pt-16 pb-16 sm:pt-20 lg:pt-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="grid size-12 place-items-center rounded-xl bg-white/10 border border-white/10">
                <Mail className="size-6 text-nexus-blue" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-[0.2em] text-nexus-blue/80">
                  {t('contact.label')}
                </div>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                  {t('contact.title')}
                </h1>
              </div>
            </div>
            <p className="text-white/70 mt-4 max-w-2xl">
              {t('contact.description')}
            </p>
          </motion.div>

          <FadeIn delay={0.1}>
            <GlassCard>
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="size-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{t('contact.success.title')}</h3>
                  <p className="text-white/70">{t('contact.success.message')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-nexus-blue/50 transition-colors"
                      placeholder={t('contact.form.name.placeholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-nexus-blue/50 transition-colors"
                      placeholder={t('contact.form.email.placeholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      {t('contact.form.company')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-nexus-blue/50 transition-colors"
                      placeholder={t('contact.form.company.placeholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      {t('contact.form.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-nexus-blue/50 transition-colors resize-none"
                      placeholder={t('contact.form.message.placeholder')}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-xl bg-nexus-blue hover:bg-nexus-blue/90 text-white px-6 py-3 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="size-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('contact.form.submitting')}
                      </>
                    ) : (
                      <>
                        <Send className="size-5" />
                        {t('contact.form.submit')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </GlassCard>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <GlassCard>
                <h3 className="text-lg font-semibold mb-2">{t('contact.info.email.title')}</h3>
                <a
                  href="mailto:contacto@nexusdevtech.com"
                  className="text-nexus-blue hover:text-nexus-cyan transition-colors"
                >
                  contacto@nexusdevtech.com
                </a>
              </GlassCard>
              <GlassCard>
                <h3 className="text-lg font-semibold mb-2">{t('contact.info.response.title')}</h3>
                <p className="text-white/70">{t('contact.info.response.desc')}</p>
              </GlassCard>
            </div>
          </FadeIn>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}

